import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { Typography, Table, Col } from "antd";
import axios from "api/axios";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { renderDpatEvidenceLink, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";
import { formatDataGeneral, getAttributeValue, getFileLinkIfExist, isDateInSelectedYear } from "utils/utils";

const { Title } = Typography;

const PUBLICATION_LINK_KEYS = ["evidence", "dispatchBook", "transmittalLetter"];

const LINK_LABELS = {
  evidence: "View Evidence",
  dispatchBook: "View Dispatch Book",
  transmittalLetter: "View Transmittal Letter",
};

const AAPPublication = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [publication, setPublication] = useState([]);
  const [fulfillment, setFulfillment] = useState("Not Fulfilled");

  const publicationColumn = useMemo(
    () => [
      { title: "Date of Publication on website", dataIndex: "date", key: "date" },
      { title: "Address of website", dataIndex: "address", key: "address" },
      { title: "Evidence Attached", dataIndex: "evidence", key: "evidence" },
      { title: "Dispatch Book", dataIndex: "dispatchBook", key: "dispatchBook" },
      { title: "Transmittal Letter", dataIndex: "transmittalLetter", key: "transmittalLetter" },
    ].map((col) =>
      PUBLICATION_LINK_KEYS.includes(col.dataIndex)
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(
              value,
              LINK_LABELS[col.dataIndex] ?? "View Document",
            ),
          }
        : col
    ),
    [],
  );

  const displayPublication = useMemo(() => {
    const rows = (publication?.data || []).filter((row) =>
      isDateInSelectedYear(row?.date, year),
    );

    if (!publication || typeof publication !== "object") {
      return { data: rows, fulfillment: "Not Fulfilled", report: [] };
    }

    return {
      ...publication,
      data: rows,
    };
  }, [publication, year]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI4",
      area: "Transparency, Accountability & Participation",
      publication: displayPublication,
    }),
  }), [displayPublication]);

  const DPAT_SLUG = "c4.0-4.2";

  const buildFileUrl = (eventUid, dataElementUid) =>
    `https://dddp.gov.gh/api/events/files?eventUid=${eventUid}&dataElementUid=${dataElementUid}`;

  const setDataDisplay = (data, reports, selectedYear) => {
    const temp = [];
    let nextFulfillment = "Not Fulfilled";

    if (data?.length > 0) {
      data.forEach((val, idx) => {
        const publishedDate = getAttributeValue("Published Date", val);
        if (!isDateInSelectedYear(publishedDate, selectedYear)) {
          return;
        }

        const evidenceLink = getFileLinkIfExist(reports, "xjRCTFFiMA3", val.trackedEntity);
        const dispatchLink = getFileLinkIfExist(reports, "S7y3qnVNe6q", val.trackedEntity);
        const transmittalLink = getFileLinkIfExist(reports, "BscTxPRQInK", val.trackedEntity);

        temp.push({
          key: idx,
          date: publishedDate,
          address: getAttributeValue("Website", val),
          evidence: evidenceLink
            ? buildFileUrl(evidenceLink, "xjRCTFFiMA3")
            : "Not Uploaded",
          dispatchBook: dispatchLink
            ? buildFileUrl(dispatchLink, "S7y3qnVNe6q")
            : "Not Uploaded",
          transmittalLetter: transmittalLink
            ? buildFileUrl(transmittalLink, "BscTxPRQInK")
            : "Not Uploaded",
        });
      });
    }

    if (temp.length > 0) {
      const linksComplete = temp.every((row) =>
        isEvidenceLinkUploaded(row.evidence)
        && isEvidenceLinkUploaded(row.dispatchBook)
        && isEvidenceLinkUploaded(row.transmittalLetter),
      );
      nextFulfillment = linksComplete ? "Fulfilled" : "Not Fulfilled";
    }

    setPublication({ data: temp, fulfillment: nextFulfillment, report: [] });
    setFulfillment(nextFulfillment);
  };

  const loadFromDhis2 = useCallback(() => {
    if (!districtId) {
      setPublication({ data: [], fulfillment: "Not Fulfilled", report: [] });
      setFulfillment("Not Fulfilled");
      return;
    }

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=X5kGqVpbGoN`)
      .then((result) => {
        const instances = result.data?.instances || [];
        if (instances.length === 0) {
          setDataDisplay([], [], year);
          return;
        }

        axios
          .get(`/tracker/events?program=X5kGqVpbGoN&orgUnit=${districtId}`)
          .then((resp) => {
            const data = formatDataGeneral(
              instances,
              "Document Published",
              "Annual Budget & Workplan",
            ) || [];
            const reports = resp.data?.instances || [];

            setDataDisplay(data, reports, year);
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log("decisions error ", err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.publication) setPublication(snap.publication);
      if (snap.publication?.fulfillment) setFulfillment(snap.publication.fulfillment);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  return (
    <Comment
      data={displayPublication}
      year={year}
      districtId={districtId}
      tableCommentedId={`c4.0-4.2-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={fulfillment} />
          </Col>

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of publication of Annual Budget & Workplan</Title>
          <Table
            columns={publicationColumn}
            dataSource={displayPublication?.data ?? []}
            pagination={false}
            bordered
            rowKey="key"
          />
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default AAPPublication;
